import { useEffect, useState, type ImgHTMLAttributes } from "react";

// Drop-in replacement for react-gif-player (unmaintained, no React 19 peer
// range). Same markup — div.gif_player(.playing) > div.play_button + img — so
// the site's `.gif_player img` styles still apply. Clicking toggles between the
// animated GIF and a still of its first frame, captured through a CORS
// preload + canvas like the library did; if the host doesn't allow that, there
// is no still and the GIF simply keeps playing.
interface GifPlayerProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  gif: string;
  still?: string;
  autoplay?: boolean;
}

function captureFirstFrame(src: string, onStill: (url: string) => void) {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext?.("2d");
      if (!ctx) return;
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      onStill(canvas.toDataURL());
    } catch {
      // Tainted canvas (no CORS headers): keep playing the GIF
    }
  };
  img.src = src;
}

export default function GifPlayer({ gif, still, autoplay = false, ...imgProps }: GifPlayerProps) {
  const [playing, setPlaying] = useState(autoplay);
  const [capturedStill, setCapturedStill] = useState<string | undefined>(undefined);

  useEffect(() => {
    setPlaying(autoplay);
    setCapturedStill(undefined);
    if (still || !gif) return;
    let current = true;
    captureFirstFrame(gif, (url) => {
      if (current) setCapturedStill(url);
    });
    return () => {
      current = false;
    };
  }, [gif, still, autoplay]);

  const stillSrc = still || capturedStill;
  const src = playing ? gif || stillSrc : stillSrc || gif;

  return (
    <div className={playing ? "gif_player playing" : "gif_player"} onClick={() => setPlaying((p) => !p)}>
      <div className="play_button" />
      <img {...imgProps} src={src} />
    </div>
  );
}
